import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

class UnpaidInvoiceService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/accounts`;
  }

  // Create axios instance with default config
  createApiInstance() {
    const instance = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });
    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return instance;
  }

  // Get unpaid invoices with filters
  async getUnpaidInvoices(filters = {}) {
    try {
      const api = this.createApiInstance();
      const params = {
        labTypeId: filters.labTypeId || '',
        transactionTypeId: filters.transactionTypeId || '',
        userName: filters.userName || '',
        orderId: filters.orderId || '',
        financialYear: filters.financialYear || '',
        dateFrom: filters.dateFrom || '',
        dateTo: filters.dateTo || '',
        approvalDateFrom: filters.approvalDateFrom || '',
        approvalDateTo: filters.approvalDateTo || '',
        page: filters.page || 1,
        pageSize: filters.pageSize || 10
      };

      const response = await api.get('/unpaid-invoices', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching unpaid invoices:', error);
      throw this.handleError(error);
    }
  }

  // Get lab types
  async getLabTypes() {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/lab-types');
      return response.data;
    } catch (error) {
      console.error('Error fetching lab types:', error);
      throw this.handleError(error);
    }
  }

  // Get transaction types
  async getTransactionTypes() {
    try {
      const api = this.createApiInstance();
      const response = await api.get('/transaction-types');
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction types:', error);
      throw this.handleError(error);
    }
  }

  // Export unpaid invoices to Excel
  async exportUnpaidInvoices(filters = {}) {
    try {
      const api = this.createApiInstance();
      const params = {
        labTypeId: filters.labTypeId || '',
        transactionTypeId: filters.transactionTypeId || '',
        userName: filters.userName || '',
        orderId: filters.orderId || '',
        financialYear: filters.financialYear || '',
        dateFrom: filters.dateFrom || '',
        dateTo: filters.dateTo || '',
        approvalDateFrom: filters.approvalDateFrom || '',
        approvalDateTo: filters.approvalDateTo || ''
      };

      const response = await api.get('/unpaid-invoices/export', { 
        params,
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'UnpaidInvoiceReport.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Export completed successfully' };
    } catch (error) {
      console.error('Error exporting unpaid invoices:', error);
      throw this.handleError(error);
    }
  }

  // Error handler
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || error.response.data?.error || 'Server error occurred';
      return {
        success: false,
        message,
        status: error.response.status,
        data: error.response.data
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        success: false,
        message: 'Network error - please check your connection',
        status: 0,
        data: null
      };
    } else {
      // Something else happened
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        status: 0,
        data: null
      };
    }
  }
}

const unpaidInvoiceService = new UnpaidInvoiceService();
export default unpaidInvoiceService;